<script setup lang="ts">
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import BaseResultAnswer from '@/components/base/BaseResultAnswer.vue';
import FormOtherChoiceResults from '@/components/ConsultationSummary/QuestionResults/FormOtherChoiceResults.vue';

import { useSummariesStore } from '@/stores/summaries.store';
import type { Locales, SummaryFormStatQuestionDto } from '@/services/types/api.types';
import { Constants } from '@/constants';

// STORE
const summariesStore = useSummariesStore();
const { formStats } = storeToRefs(summariesStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
}>();

// CONST
const { t } = useI18n();

// COMPUTED
const choices = computed(() => {
  const formStat = formStats?.value?.get(props.formId);
  switch (formStat?.type) {
    case Constants.Form.Type.Question: {
      const stats = formStat?.stats as unknown as SummaryFormStatQuestionDto;
      return stats?.choices ? stats.choices : null;
    }
    default:
      return null;
  }
});

const questionHasOtherVotes = computed((): boolean => !!choices.value?.find((choice) => choice.id === 'o'));

const questionHasChoiceWithImage = computed((): boolean => !!choices.value?.some((choice) => choice.image));

const isConfigured = computed((): boolean => (choices.value?.length ?? 0) > 0);

// WATCH
watch(
  questionHasOtherVotes,
  (newVal) => {
    if (newVal && props.formId) {
      summariesStore.getFormOtherChoices(props.formId);
    }
  },
  { immediate: true },
);
</script>
<template>
  <div class="form-multi-choice-question-results">
    <div v-if="isConfigured">
      <BaseResultAnswer
        v-for="choice in choices"
        :key="choice.id"
        :choice="choice"
        :language-enabled="props.languageEnabled"
        :question-has-choice-with-image="questionHasChoiceWithImage"
      />
      <FormOtherChoiceResults
        v-if="questionHasOtherVotes"
        :form-id="props.formId"
        :language-enabled="props.languageEnabled"
      />
    </div>
    <div v-else class="text">
      {{ t('form.summary.status.not-configured') }}
    </div>
  </div>
</template>
<style lang="scss">
.form-multi-choice-question-results {
  .text {
    color: $color-neutral-grey-400;
    font-size: $small-text;
  }
}
</style>
